<template>
  <header
    class="pos-header"
    :style="{
      background: theme.background,
      color: theme.text
    }"
  >
    <!-- ======================================================
         LEFT RAIL HEADER
    ======================================================= -->

    <div class="pos-header-rail">
      <v-tooltip
        :text="
          currentLanguage === 'fr'
            ? 'Menu'
            : 'Menu'
        "
        location="bottom"
      >
        <template #activator="{ props: tooltipProps }">
          <button
            v-bind="tooltipProps"
            type="button"
            class="header-rail-button"
            @click="emit('toggle-sidebar')"
          >
            <v-icon size="21">
              mdi-menu
            </v-icon>
          </button>
        </template>
      </v-tooltip>
    </div>

    <!-- ======================================================
         HEADER CONTENT
    ======================================================= -->

    <div class="pos-header-content">
      <!-- ====================================================
           BRAND
      ===================================================== -->

      <router-link
        :to="homeRoute"
        class="pos-brand"
      >
        <v-img
          v-if="currentLogo"
          :src="currentLogo"
          class="pos-logo"
          contain
        />

        <div
          v-else
          class="fallback-brand"
        >
          Nexgbits POS
        </div>
      </router-link>

      <!-- ====================================================
           TERMINAL
      ===================================================== -->

      <div
        v-if="mdAndUp"
        class="header-status-block"
      >
        <div
          class="header-status-icon"
          :class="{
            active: hasTerminal
          }"
        >
          <v-icon size="20">
            {{
              hasTerminal
                ? "mdi-monitor"
                : "mdi-monitor-off"
            }}
          </v-icon>
        </div>

        <div class="header-status-text">
          <div class="header-status-label">
            Terminal
          </div>

          <div class="header-status-value">
            {{
              hasTerminal
                ? (
                    terminalCode &&
                    terminalName
                      ? `${terminalCode} - ${terminalName}`
                      : (
                          terminalName ||
                          terminalCode ||
                          "Terminal"
                        )
                  )
                : (
                    currentLanguage === "fr"
                      ? "Non configuré"
                      : "Not configured"
                  )
            }}
          </div>
        </div>
      </div>

      <!-- ====================================================
           SESSION
      ===================================================== -->

      <div
        v-if="mdAndUp"
        class="header-status-block"
      >
        <div
          class="header-status-icon session"
          :class="{
            active: hasOpenSession
          }"
        >
          <v-icon size="20">
            {{
              hasOpenSession
                ? "mdi-account-clock"
                : "mdi-account-clock-outline"
            }}
          </v-icon>
        </div>

        <div class="header-status-text">
          <div class="header-status-label">
            Session
          </div>

          <div class="header-status-value">
            {{
              hasOpenSession
                ? sessionNumber
                : (
                    currentLanguage === "fr"
                      ? "Aucune session"
                      : "No active session"
                  )
            }}
          </div>
        </div>
      </div>

      <v-spacer />

      <!-- ====================================================
           CONNECTIVITY
      ===================================================== -->

      <div
        class="connection-status"
        :class="{
          online: isOnline,
          offline: !isOnline
        }"
      >
        <span class="connection-dot" />

        <span class="connection-text">
          {{
            isOnline
              ? (
                  currentLanguage === "fr"
                    ? "En ligne"
                    : "Online"
                )
              : (
                  currentLanguage === "fr"
                    ? "Hors ligne"
                    : "Offline"
                )
          }}
        </span>
      </div>

      <!-- ====================================================
           SYNC QUEUE
      ===================================================== -->

      <v-btn
        v-if="mdAndUp"
        variant="outlined"
        color="secondary"
        size="small"
        class="header-action-button sync-button"
        prepend-icon="mdi-sync"
        @click="requestSyncQueue"
      >
        Sync Queue
      </v-btn>

      <!-- ====================================================
           TERMINAL / SESSION ACTION
      ===================================================== -->

      <v-btn
        v-if="
          mdAndUp &&
          !hasTerminal
        "
        color="primary"
        variant="flat"
        size="small"
        class="header-action-button"
        prepend-icon="mdi-monitor-plus"
        :disabled="sessionBusy"
        @click="emit('open-terminal')"
      >
        {{
          currentLanguage === "fr"
            ? "Ouvrir le terminal"
            : "Open Terminal"
        }}
      </v-btn>

      <v-btn
        v-else-if="
          mdAndUp &&
          hasTerminal &&
          !hasOpenSession
        "
        color="success"
        variant="flat"
        size="small"
        class="header-action-button"
        prepend-icon="mdi-play-circle-outline"
        :loading="sessionBusy"
        :disabled="sessionBusy"
        @click="emit('start-session')"
      >
        {{
          currentLanguage === "fr"
            ? "Démarrer"
            : "Start Session"
        }}
      </v-btn>

      <v-btn
        v-else-if="
          mdAndUp &&
          hasOpenSession
        "
        color="error"
        variant="outlined"
        size="small"
        class="header-action-button"
        prepend-icon="mdi-stop-circle-outline"
        :loading="sessionBusy"
        :disabled="sessionBusy"
        @click="emit('end-session')"
      >
        {{
          currentLanguage === "fr"
            ? "Terminer"
            : "End Session"
        }}
      </v-btn>

      <!-- ====================================================
           THEME
      ===================================================== -->

      <v-menu
        v-model="themeMenu"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="small"
            variant="text"
            class="header-icon-button"
          >
            <v-icon size="19">
              mdi-theme-light-dark
            </v-icon>
          </v-btn>
        </template>

        <v-card min-width="260">
          <v-card-title class="text-subtitle-2">
            {{
              currentLanguage === "fr"
                ? "Apparence"
                : "Appearance"
            }}
          </v-card-title>

          <v-divider />

          <v-list density="compact">
            <v-list-item
              prepend-icon="mdi-white-balance-sunny"
              title="Light"
              @click="selectTheme('light')"
            >
              <template #append>
                <v-icon
                  v-if="currentTheme === 'light'"
                  color="primary"
                >
                  mdi-check
                </v-icon>
              </template>
            </v-list-item>

            <v-list-item
              prepend-icon="mdi-weather-night"
              title="Dark"
              @click="selectTheme('dark')"
            >
              <template #append>
                <v-icon
                  v-if="currentTheme === 'dark'"
                  color="primary"
                >
                  mdi-check
                </v-icon>
              </template>
            </v-list-item>

            <v-list-item
              prepend-icon="mdi-theme-light-dark"
              title="Hybrid"
              @click="selectTheme('hybrid')"
            >
              <template #append>
                <v-icon
                  v-if="currentTheme === 'hybrid'"
                  color="primary"
                >
                  mdi-check
                </v-icon>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- ====================================================
           LANGUAGE
      ===================================================== -->

      <LangSwitch />

      <!-- ====================================================
           MESSAGES
      ===================================================== -->

      <v-btn
        icon
        size="small"
        variant="text"
        class="header-icon-button"
      >
        <v-badge
          v-if="messageCount > 0"
          :content="messageCount"
          color="error"
        >
          <v-icon size="19">
            mdi-email-outline
          </v-icon>
        </v-badge>

        <v-icon
          v-else
          size="19"
        >
          mdi-email-outline
        </v-icon>
      </v-btn>

      <!-- ====================================================
           ACCOUNT
      ===================================================== -->

      <v-menu>
        <template #activator="{ props }">
          <button
            v-bind="props"
            type="button"
            class="account-button"
          >
            <div class="account-avatar">
              <v-icon size="18">
                mdi-account
              </v-icon>
            </div>

            <span
              v-if="mdAndUp"
              class="account-name"
            >
              {{ displayName }}
            </span>

            <v-icon size="16">
              mdi-chevron-down
            </v-icon>
          </button>
        </template>

        <v-list density="compact">
          <v-list-item
            prepend-icon="mdi-account-outline"
            :title="
              currentLanguage === 'fr'
                ? 'Mon profil'
                : 'My Profile'
            "
          />

          <v-divider />

          <v-list-item
            prepend-icon="mdi-logout-variant"
            :title="
              currentLanguage === 'fr'
                ? 'Déconnexion'
                : 'Logout'
            "
            @click="handleLogout"
          />
        </v-list>
      </v-menu>
    </div>

    <!-- ======================================================
         MOBILE SESSION ACTION BAR
    ======================================================= -->

    <div
      v-if="!mdAndUp"
      class="mobile-session-bar"
    >
      <div class="mobile-terminal">
        <v-icon
          size="16"
          class="mr-2"
        >
          {{
            hasTerminal
              ? "mdi-monitor"
              : "mdi-monitor-off"
          }}
        </v-icon>

        <div class="mobile-terminal-details">
          <strong>
            {{
              hasTerminal
                ? (
                    terminalCode ||
                    terminalName ||
                    "POS"
                  )
                : (
                    currentLanguage === "fr"
                      ? "Aucun terminal"
                      : "No Terminal"
                  )
            }}
          </strong>

          <span
            v-if="hasOpenSession"
            class="mobile-session-number"
          >
            {{ sessionNumber }}
          </span>
        </div>
      </div>

      <v-spacer />

      <v-btn
        v-if="!hasTerminal"
        color="primary"
        size="x-small"
        variant="flat"
        :disabled="sessionBusy"
        @click="emit('open-terminal')"
      >
        Open Terminal
      </v-btn>

      <v-btn
        v-else-if="!hasOpenSession"
        color="success"
        size="x-small"
        variant="flat"
        :loading="sessionBusy"
        :disabled="sessionBusy"
        @click="emit('start-session')"
      >
        Start Session
      </v-btn>

      <v-btn
        v-else
        color="error"
        size="x-small"
        variant="outlined"
        :loading="sessionBusy"
        :disabled="sessionBusy"
        @click="emit('end-session')"
      >
        End Session
      </v-btn>
    </div>
  </header>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref
} from "vue";

import {
  useRouter
} from "vue-router";

import {
  useDisplay
} from "vuetify";

import LangSwitch
  from "@/components/LangSwitch.vue";

import {
  useAuthStore,
  useThemeStore
} from "@/store/auth";

import {
  useLangStore
} from "@/store/lang";

import offlineManager
  from "@/platform/pwa/offlineManager";

import eventBus
  from "@/platform/core/eventBus";

// ============================================================
// PROPS
// ============================================================

const props =
  defineProps({
    pinned: {
      type: Boolean,
      default: false
    },

    terminal: {
      type: Object,
      default: null
    },

    session: {
      type: Object,
      default: null
    },

    sessionBusy: {
      type: Boolean,
      default: false
    }
  });

// ============================================================
// EVENTS
// ============================================================

const emit =
  defineEmits([
    "toggle-sidebar",
    "toggle-pin",

    "open-terminal",
    "close-terminal",

    "start-session",
    "end-session",
    "open-sync-queue"
  ]);

// ============================================================
// STORES
// ============================================================

const auth =
  useAuthStore();

const themeStore =
  useThemeStore();

const langStore =
  useLangStore();

const router =
  useRouter();

const {
  mdAndUp
} =
  useDisplay();

// ============================================================
// STATE
// ============================================================

const themeMenu =
  ref(false);

const messageCount =
  ref(0);

// ============================================================
// CONNECTIVITY
//
// offlineManager is the authoritative application-wide
// connectivity state.
//
// PosHeader must not maintain an independent navigator.onLine
// state because the backend may be unreachable while the
// browser still reports a network interface as connected.
// ============================================================

const isOnline =
  ref(
    offlineManager
      .isOnlineNow()
  );

let unsubscribeConnectivity =
  null;

// ============================================================
// LANGUAGE
// ============================================================

const currentLanguage =
  computed(() =>
    langStore.currentLanguage ||
    "en"
  );

// ============================================================
// THEME
// ============================================================

const theme =
  computed(() =>
    themeStore.themeStyles
  );

const currentTheme =
  computed(() =>
    localStorage.getItem(
      "currentTheme"
    ) ||
    "light"
  );

function selectTheme(
  selectedTheme
) {
  themeStore.setTheme(
    selectedTheme
  );

  localStorage.setItem(
    "currentTheme",
    selectedTheme
  );

  themeMenu.value =
    false;
}

// ============================================================
// LOGO
// ============================================================

const currentLogo =
  computed(() => {
    const selectedTheme =
      localStorage.getItem(
        "currentTheme"
      ) ||
      "light";

    if (
      selectedTheme ===
      "dark"
    ) {
      return (
        localStorage.getItem(
          "LogoDark"
        ) ||
        ""
      );
    }

    return (
      localStorage.getItem(
        "LogoLight"
      ) ||
      ""
    );
  });

// ============================================================
// USER
// ============================================================

const displayName =
  computed(() =>
    auth.loginName
    ||
    auth.user?.name
    ||
    "Cashier"
  );

// ============================================================
// TERMINAL
// ============================================================

const hasTerminal =
  computed(() =>
    Boolean(
      props.terminal?.id
      ||
      props.terminal?.terminalId
    )
  );

const terminalName =
  computed(() =>
    props.terminal?.terminalName
    ??
    props.terminal?.name
    ??
    ""
  );

const terminalCode =
  computed(() =>
    props.terminal?.terminalCode
    ??
    props.terminal?.code
    ??
    props.session?.terminalCode
    ??
    ""
  );

// ============================================================
// SESSION
// ============================================================

// ============================================================
// SESSION LIFECYCLE STATUS
//
// A session ID alone does not mean that the session is open.
// Treat explicitly closed sessions as inactive. Unknown legacy
// statuses remain active until the backend confirms closure,
// preventing accidental concurrent sessions.
// ============================================================

function isSessionOpen(session) {
  if (!session?.id && !session?.sessionId) {
    return false;
  }

  if (
    session.closedAtUtc ||
    session.closedAt ||
    session.closedOnUtc ||
    session.endedAtUtc
  ) {
    return false;
  }

  const status = String(
    session.status ?? session.sessionStatus ?? ""
  ).trim().toUpperCase();

  return ![
    "CLOSED",
    "CLOSE",
    "CANCELLED",
    "CANCELED",
    "ENDED"
  ].includes(status);
}

const hasOpenSession =
  computed(() => isSessionOpen(props.session));

const sessionNumber =
  computed(() =>
    props.session?.sessionNumber
    ??
    props.session?.number
    ??
    (
      hasOpenSession.value
        ? (
            currentLanguage.value === "fr"
              ? "Session active"
              : "Active Session"
          )
        : ""
    )
  );

// ============================================================
// HOME
// ============================================================

const homeRoute =
  computed(() => ({
    name:
      "PublicHome",

    params: {
      lang:
        currentLanguage.value
    }
  }));

// ============================================================
// CONNECTIVITY STATUS HANDLER
//
// All connectivity updates arrive through:
//
// platform.offline.changed
//
// This can be triggered by:
// - browser offline
// - browser online
// - real network request failure
// - successful backend recovery
// ============================================================

function applyConnectivityStatus(
  status
) {
  if (
    typeof status?.isOnline ===
    "boolean"
  ) {
    isOnline.value =
      status.isOnline;

    return;
  }

  isOnline.value =
    offlineManager
      .isOnlineNow();
}

// ============================================================
// LOGOUT
// ============================================================

async function handleLogout() {
  await auth.logout();

  await router.push({
    name:
      "PublicHome",

    params: {
      lang:
        currentLanguage.value
    }
  });
}

function requestSyncQueue() {
  console.log(
    "[POS HEADER] Sync Queue requested."
  );

  eventBus.emit(
    "pos.sync.queue.open"
  );
}
// ============================================================
// LIFECYCLE
// ============================================================

onMounted(
  () => {
    /*
     * Safe to call even if the platform boot process already
     * initialized OfflineManager.
     */
    offlineManager.init();

    /*
     * Read the centralized state immediately.
     */
    isOnline.value =
      offlineManager
        .isOnlineNow();

    /*
     * Listen to all centralized connectivity changes.
     */
    unsubscribeConnectivity =
      eventBus.on(
        "platform.offline.changed",
        status => {
          applyConnectivityStatus(
            status
          );
        }
      );

    /*
     * Refresh once after the listener exists.
     *
     * The updated OfflineManager retains a confirmed network
     * failure rather than blindly trusting navigator.onLine.
     */
    offlineManager.refresh();

    isOnline.value =
      offlineManager
        .isOnlineNow();

    console.log(
      "[POS] Header connectivity initialized:",
      offlineManager
        .getStatus()
    );
  }
);

onBeforeUnmount(
  () => {
    if (
      unsubscribeConnectivity
    ) {
      unsubscribeConnectivity();

      unsubscribeConnectivity =
        null;
    }
  }
);
</script>

<style scoped>
/* ============================================================
   ROOT
============================================================ */

.pos-header {
  width: 100%;

  min-height: 68px;

  position: relative;

  z-index: 1000;

  display: flex;
  align-items: stretch;

  border-bottom:
    1px solid
    rgba(
      15,
      23,
      42,
      0.08
    );

  background: #ffffff !important;
}

/* ============================================================
   LEFT RAIL HEADER
============================================================ */

.pos-header-rail {
  width: 56px;
  min-width: 56px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #202328;

  color: #ffffff;

  border-right:
    1px solid
    rgba(
      255,
      255,
      255,
      0.04
    );
}

.header-rail-button {
  width: 56px;
  height: 68px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: 0;

  background: transparent;

  color:
    rgba(
      255,
      255,
      255,
      0.88
    );

  cursor: pointer;

  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.header-rail-button:hover {
  background:
    rgba(
      255,
      255,
      255,
      0.08
    );

  color: #ffffff;
}

/* ============================================================
   MAIN HEADER CONTENT
============================================================ */

.pos-header-content {
  flex:
    1
    1
    auto;

  min-width: 0;

  height: 68px;

  padding:
    0
    20px;

  display: flex;
  align-items: center;

  gap: 16px;

  background: #ffffff;

  color: #162033;
}

/* ============================================================
   BRAND
============================================================ */

.pos-brand {
  flex-shrink: 0;

  min-width: 145px;

  display: flex;
  align-items: center;

  text-decoration: none;
}

.pos-logo {
  width: 145px;
  height: 36px;
}

.fallback-brand {
  color: #111c35;

  font-size: 16px;
  font-weight: 800;

  white-space: nowrap;
}

/* ============================================================
   STATUS BLOCK
============================================================ */

.header-status-block {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 8px;
}

.header-status-icon {
  width: 36px;
  height: 36px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;

  background:
    rgba(
      15,
      23,
      42,
      0.05
    );

  color: #27364d;
}

.header-status-icon.active {
  background:
    rgba(
      37,
      99,
      235,
      0.08
    );

  color:
    rgb(
      var(--v-theme-primary)
    );
}

.header-status-icon.session.active {
  background:
    rgba(
      37,
      99,
      235,
      0.08
    );
}

.header-status-text {
  min-width: 0;
}

.header-status-label {
  color: #5f6980;

  font-size: 11px;
  font-weight: 500;

  line-height: 14px;
}

.header-status-value {
  max-width: 180px;

  overflow: hidden;

  color: #162033;

  font-size: 12px;
  font-weight: 700;

  line-height: 16px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ============================================================
   CONNECTION
============================================================ */

.connection-status {
  flex-shrink: 0;

  display: flex;
  align-items: center;

  gap: 6px;

  font-size: 12px;
  font-weight: 700;

  white-space: nowrap;
}

.connection-dot {
  width: 8px;
  height: 8px;

  border-radius: 50%;
}

.connection-status.online {
  color: #159447;
}

.connection-status.online
.connection-dot {
  background: #20b95a;
}

.connection-status.offline {
  color: #d33131;
}

.connection-status.offline
.connection-dot {
  background: #e23d3d;
}

/* ============================================================
   HEADER BUTTONS
============================================================ */

.header-action-button {
  min-height: 36px;

  border-radius: 6px;

  font-size: 11px;
  font-weight: 700;

  letter-spacing: 0.25px;
}

.sync-button {
  min-width: 125px;
}

.header-icon-button {
  flex-shrink: 0;
}

/* ============================================================
   ACCOUNT
============================================================ */

.account-button {
  min-height: 40px;

  padding:
    0
    4px
    0
    6px;

  display: flex;
  align-items: center;

  gap: 6px;

  border: 0;

  background: transparent;

  color: #162033;

  cursor: pointer;
}

.account-avatar {
  width: 32px;
  height: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background:
    rgba(
      15,
      23,
      42,
      0.08
    );

  color: #27364d;
}

.account-name {
  max-width: 120px;

  overflow: hidden;

  font-size: 11px;
  font-weight: 600;

  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ============================================================
   MOBILE SESSION BAR
============================================================ */

.mobile-session-bar {
  width: 100%;

  min-height: 40px;

  padding:
    4px
    10px;

  display: flex;
  align-items: center;

  border-top:
    1px solid
    rgba(
      15,
      23,
      42,
      0.06
    );

  background: #ffffff;
}

.mobile-terminal {
  min-width: 0;

  display: flex;
  align-items: center;

  color: #162033;

  font-size: 11px;
}

.mobile-terminal-details {
  min-width: 0;

  display: flex;
  flex-direction: column;
}

.mobile-session-number {
  max-width: 140px;

  overflow: hidden;

  color: #6d7586;

  font-size: 9px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ============================================================
   MEDIUM DESKTOP
============================================================ */

@media (
  max-width: 1280px
) {
  .pos-header-content {
    padding:
      0
      12px;

    gap: 10px;
  }

  .pos-brand {
    min-width: 120px;
  }

  .pos-logo {
    width: 120px;
  }

  .header-status-value {
    max-width: 130px;
  }

  .sync-button {
    min-width: auto;
  }

  .account-name {
    display: none;
  }
}

/* ============================================================
   MOBILE / TABLET
============================================================ */

@media (
  max-width: 959px
) {
  .pos-header {
    display: block;
  }

  .pos-header-rail {
    position: absolute;

    top: 0;
    left: 0;

    height: 58px;
  }

  .header-rail-button {
    height: 58px;
  }

  .pos-header-content {
    height: 58px;

    padding:
      0
      8px
      0
      66px;

    gap: 4px;
  }

  .pos-brand {
    min-width: 0;
  }

  .pos-logo {
    width: 120px;
    height: 34px;
  }

  .connection-text {
    display: none;
  }
}

/* ============================================================
   SMALL MOBILE
============================================================ */

@media (
  max-width: 599px
) {
  .pos-logo {
    width: 105px;
  }

  .header-icon-button {
    width: 32px;
    height: 32px;
  }
}
</style>